import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';

import { C } from '../constants/theme';
import { Btn, CheckBox, Heading, OptionCard, Radio, Sub } from '../components';
import { toggleArr } from '../helpers';
import { LIST_AGE_RANGES, LIST_FOOD_MOTIVATIONS } from 'core/api/apollo/queries';
import { SET_HEALTH_PROFILE, SET_GENDER, SET_AGE_RANGE, SET_FOOD_MOTIVATION } from 'core/api/apollo/mutations';
import { useProgramContext } from 'core/api/programContext';

const Phase2Lifestyle = ({ goPhase }) => {
  const {
    gender, setGender,
    activity, setActivity,
    training, setTraining,
    glp1, setGlp1,
    foodRel, setFoodRel,
    // household, setHousehold,
  } = useProgramContext();
  const genderList = [{ v: 'FEMALE', l: 'Female' }, { v: 'MALE', l: 'Male' }, { v: 'OTHER', l: 'Prefer not to say' }];
  const activityList = [
    { v: 'SEDENTARY', l: 'Sedentary', d: 'Desk job, minimal exercise, <6k steps / day' },
    { v: 'MODERATE', l: 'Moderate', d: '3-4x workouts / week and/or 6-10k steps / day' },
    { v: 'ACTIVE', l: 'Active', d: '5-6 workouts / week and/or 11-15k steps / day' },
    { v: 'ATHLETE', l: 'Athlete', d: 'Competitive, 2-a-days, and/or 16k+ steps / day' },
  ];
  const trainingList = [
    { v: 'CARDIO_FOCUSED', l: 'Cardio-focused', d: 'Running, cycling, HIIT' },
    { v: 'STRENGTH_FOCUSED', l: 'Strength-focused', d: 'Weightlifting, Pilates, powerlifting, CrossFit' },
    { v: 'MIXED_BALANCED', l: 'Mixed / Balanced', d: 'Cardio and strength' },
    { v: 'SPORT_SPECIFIC', l: 'Sport-specific', d: 'Team sports, martial arts, tennis' },
  ];

  const glp1List = [
    { v: true, l: 'Yes' },
    { v: false, l: 'No' },
  ];

  const [ageRange, setAgeRange] = useState(null);

  const { data: { listAgeRanges = [] } = {} } = useQuery(LIST_AGE_RANGES);
  const { data: { listFoodMotivations = [] } = {} } = useQuery(LIST_FOOD_MOTIVATIONS);

  const [setFoodMotivations] = useMutation(SET_FOOD_MOTIVATION);

  const handleSetFoodMotivation = async () => {
    try {
      await setFoodMotivations({
        variables: {
          input: {
            foodMotivationIds: foodRel,
          },
        },
      });
    } catch (error) {
      console.error('Failed to set food motivation:', error);
      throw error;
    }
  };

  const [setUserAgeRange] = useMutation(SET_AGE_RANGE);

  const handleSetAgeRange = async () => {
    try {
      await setUserAgeRange({
        variables: {
          input: {
            ageRangeId: ageRange?.id,
          },
        },
      });
    } catch (error) {
      console.error('Failed to set age range:', error);
      throw error;
    }
  };

  const [setUserGender] = useMutation(SET_GENDER);

  const handleSetGender = async () => {
    try {
      await setUserGender({
        variables: {
          input: {
            gender: gender,
          },
        },
      });
    } catch (error) {
      console.error('Failed to set gender:', error);
      throw error;
    }
  };

  const [setHealthProfile] = useMutation(SET_HEALTH_PROFILE);

  const handleSetHealthGoals = async () => {
    try {
      await setHealthProfile({
        variables: {
          input: {
            activityLevel: activity,
            trainingType: training,
            // householdSize: household, removed as per feedback
            onGlp1Medication: glp1,
          },
        },
      });
    } catch (error) {
      console.error('Failed to set health profiles:', error);
      throw error;
    }
  };


  const handleContinue = async () => {
    try {
      await Promise.all([
        handleSetFoodMotivation(),
        handleSetAgeRange(),
        handleSetGender(),
        handleSetHealthGoals(),
      ]);
      goPhase(3, 0);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save lifestyle data:', error);
    }
  };

  const RenderGender = () => (
    <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
      {genderList.map((o) =>
        <OptionCard key={o.v} active={gender === o.v} onClick={() => setGender(o.v)}
          style={{ flex: 1, justifyContent: 'center', padding: '12px' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.darkBrown }}>{o.l}</span>
        </OptionCard>
      )}
    </div>
  );

  const RenderAgeRanges = () => {
    return listAgeRanges && listAgeRanges.map((age) => (
      <OptionCard key={age.id} active={ageRange?.id === age?.id} onClick={() => setAgeRange(age)}
        style={{ flex: 1, justifyContent: 'center', padding: '10px 8px' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.darkBrown }}>{age?.name}</span>
      </OptionCard>
    ));
  };

  const RenderActivityLevels = () => {
    return activityList.map((o) =>
      <OptionCard key={o.v} active={activity === o.v} onClick={() => setActivity(o.v)}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.darkBrown }}>{o.l}</div>
          <div style={{ fontSize: 11, color: C.lightBrown }}>{o.d}</div>
        </div>
        <Radio active={activity === o.v}/>
      </OptionCard>
    );
  };

  const RenderTraining = () => {
    if (activity === 'MODERATE' || activity === 'ACTIVE' || activity === 'ATHLETE') {
      return (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.darkBrown, marginBottom: 8 }}>What does your training look like?</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
            {trainingList.map((o) =>
              <OptionCard key={o.v} active={training === o.v} onClick={() => setTraining(o.v)}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.darkBrown }}>{o.l}</div>
                  <div style={{ fontSize: 11, color: C.lightBrown }}>{o.d}</div>
                </div>
                <Radio active={training === o.v}/>
              </OptionCard>
            )}
          </div>
        </>
      );
    } else {
      return null;
    }
  };

  const RenderGLPMedication = () => {
    return glp1List.map((o) =>
      <OptionCard key={String(o.v)} active={glp1 === o.v} onClick={() => setGlp1(o.v)} style={{ flex: 1 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.darkBrown }}>{o.l}</span>
      </OptionCard>
    );
  };

  // toggleArr = (arr, set, id, max)
  const RenderFoodMotivations = () => {
    return listFoodMotivations && listFoodMotivations.map((o) =>
      <OptionCard key={o.id} active={foodRel.includes(o.id)} onClick={() => toggleArr(foodRel, setFoodRel, o.id, 2)}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.darkBrown }}>{o.name}</div>
          <div style={{ fontSize: 11, color: C.lightBrown }}>{o.description}</div>
        </div>
        <CheckBox active={foodRel.includes(o.id)}/>
      </OptionCard>
    );
  };

  // const RenderHouseHold = () => {
  //   const houseHoldList = [{ value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4+' }];
  //   return houseHoldList.map((h) =>
  //     <OptionCard key={h.value} active={household === h.value} onClick={() => setHousehold(h.value)}
  //       style={{ flex: 1, justifyContent: 'center', padding: '12px' }}>
  //       <span style={{ fontSize: 15, fontWeight: 600, color: C.darkBrown }}>{h.label}</span>
  //     </OptionCard>
  //   );
  // };

  return <>
    <Heading>Tell us about you</Heading>
    <Sub>This helps us dial in your portions, protein targets, and the right program for your body.</Sub>
    <div style={{ fontSize: 13, fontWeight: 700, color: C.darkBrown, marginBottom: 8 }}>
      Who are you building this plan for?
    </div>
    <RenderGender />

    <div style={{ fontSize: 13, fontWeight: 700, color: C.darkBrown, marginBottom: 8 }}>Age Range</div>
    <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
      <RenderAgeRanges />
    </div>

    <div style={{ fontSize: 13, fontWeight: 700, color: C.darkBrown, marginBottom: 8 }}>Current Activity Level</div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
      <RenderActivityLevels />
    </div>

    <RenderTraining />

    <div style={{ fontSize: 13, fontWeight: 700, color: C.darkBrown, marginBottom: 8 }}>
      Are you on a GLP-1 medication?
    </div>
    <div style={{ fontSize: 13, fontWeight: 700, color: C.darkBrown, marginBottom: 8 }}>
      Examples: Ozempic, Wegovy, Mounjaro, Zepbound, Saxenda, Rybelsus, Trulicity, or any other GLP-1 receptor agonist
    </div>
    <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
      <RenderGLPMedication />
    </div>

    <div style={{ fontSize: 13, fontWeight: 700, color: C.darkBrown, marginBottom: 4 }}>
      What's driving your next chapter with food?
    </div>
    <div style={{ fontSize: 11, color: C.lightBrown, marginBottom: 8 }}>Select up to 2.</div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
      <RenderFoodMotivations />
    </div>

    {/* <div style={{ fontSize: 13, fontWeight: 700, color: C.darkBrown, marginBottom: 4 }}>How many people are in your household?</div> */}
    {/* <div style={{ fontSize: 12, color: C.lightBrown, marginBottom: 8 }}>This helps us understand your lifestyle—this plan is just for you.</div> */}
    {/* <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}> */}
      {/* <RenderHouseHold /> */}
    {/* </div> */}

    <Btn
      onClick={handleContinue}
      disabled={!gender || !activity || !ageRange}
      style={{ width: '100%' }}>Continue <ChevronRight size={15}/>
    </Btn>
  </>;
};

Phase2Lifestyle.propTypes = {
  goPhase: PropTypes.func.isRequired,
};

export default Phase2Lifestyle;
